import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Briefcase, X, Plus, AlertTriangle, Trash2, CheckCircle, FileText, Upload, Lock, Download, Shield, Loader2, Eye, EyeOff, RefreshCw, ArrowLeftRight } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import FileUploadPlaceholder from '../components/FileUploadPlaceholder';
import { ProfessionalVerificationPanel } from '../components/ProfessionalVerificationPanel';
import IDVerificationForm from '../components/IDVerificationForm';
import { ProfessionalServiceCoverageEditor } from '../components/ProfessionalServiceCoverageEditor';

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user, API, language, logout, activeRole } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [userData, setUserData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  const [proData, setProData] = useState({
    skills: [],
    certifications: [],
    hourly_rate: '',
    bio: '',
    languages: ['en'],
    categories: [],
    service_areas: { type: 'cantons', values: [] }
  });
  const [newSkill, setNewSkill] = useState('');
  const [newCert, setNewCert] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [documents, setDocuments] = useState({
    profile_photo_url: null,
    company_logo_url: null,
    business_license_url: null,
    id_document_url: null,
    insurance_url: null,
    id_verification: { country: '', documentType: '', documentUrl: '' }
  });
  const [exportingData, setExportingData] = useState(false);
  const [showDataRequest, setShowDataRequest] = useState(false);
  const [dataRequestReason, setDataRequestReason] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [autoRenewal, setAutoRenewal] = useState(false);
  const [togglingAutoRenewal, setTogglingAutoRenewal] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [removingCard, setRemovingCard] = useState(false);
  const [profileRevision, setProfileRevision] = useState(0);
  const hasProfessionalProfile = user?.role === 'professional' || user?.roles?.includes('professional') || user?.pending_role_setup === 'professional';

  useEffect(() => {
    fetchUserData();
    fetchSubscriptionStatus();
    fetchSavedCard();
    if (hasProfessionalProfile) {
      fetchProfessionalProfile();
    }
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/status`, { params: { role: activeRole || user?.role } });
      setSubscriptionStatus(response.data);
      
      // Also fetch auto-renewal status
      const autoRenewalResponse = await axios.get(`${API}/subscription/auto-renewal`);
      setAutoRenewal(autoRenewalResponse.data.auto_renewal || false);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const fetchSavedCard = async () => {
    try {
      const response = await axios.get(`${API}/saferpay/saved-card`);
      setSavedCard(response.data);
    } catch (error) {
      console.error('Failed to fetch saved card:', error);
    }
  };

  const handleRemoveCard = async () => {
    setRemovingCard(true);
    try {
      await axios.delete(`${API}/saferpay/saved-card`);
      setSavedCard({ has_card: false, card_display: null });
      setAutoRenewal(false);
      toast.success(t.cardRemoved);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to remove card' :
                  language === 'it' ? 'Impossibile rimuovere la carta' :
                  language === 'fr' ? 'Impossible de supprimer la carte' :
                  'Karte konnte nicht entfernt werden');
    } finally {
      setRemovingCard(false);
    }
  };

  const handleToggleAutoRenewal = async () => {
    setTogglingAutoRenewal(true);
    try {
      const newValue = !autoRenewal;
      await axios.put(`${API}/subscription/auto-renewal`, { auto_renewal: newValue });
      setAutoRenewal(newValue);
      toast.success(newValue ? t.autoRenewalEnabled : t.autoRenewalDisabled);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to update auto-renewal' : 
                  language === 'it' ? 'Impossibile aggiornare il rinnovo automatico' :
                  language === 'fr' ? 'Impossible de mettre à jour le renouvellement automatique' :
                  'Automatische Verlängerung konnte nicht aktualisiert werden');
    } finally {
      setTogglingAutoRenewal(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Check 7-day window first
    if (subscriptionStatus?.can_cancel?.allowed === false && subscriptionStatus?.can_cancel?.reason === 'within_7_days') {
      const days = subscriptionStatus.can_cancel.days_left;
      alert(
        language === 'de' ? `Kündigung nicht möglich. Ihr Abonnement verlängert sich in ${days} Tag(en). Das Kündigungsfenster ist geschlossen.` :
        language === 'it' ? `Cancellazione non possibile. Il tuo abbonamento si rinnova tra ${days} giorno/i. La finestra di cancellazione è chiusa.` :
        language === 'fr' ? `Annulation impossible. Votre abonnement se renouvelle dans ${days} jour(s). La fenêtre d'annulation est fermée.` :
        `Cannot cancel. Your subscription renews in ${days} day(s). The cancellation window is closed.`
      );
      return;
    }

    const confirmMsgs = {
      en: 'Are you sure you want to cancel your subscription? You will keep access until the end of your billing period.',
      de: 'Möchten Sie Ihr Abonnement wirklich kündigen? Sie behalten den Zugang bis zum Ende Ihres Abrechnungszeitraums.',
      it: 'Sei sicuro di voler annullare? Manterrai l\'accesso fino alla fine del periodo di fatturazione.',
      fr: 'Êtes-vous sûr de vouloir annuler? Vous conserverez l\'accès jusqu\'à la fin de votre période de facturation.'
    };

    if (!window.confirm(confirmMsgs[language] || confirmMsgs.en)) return;

    setCancellingSubscription(true);
    try {
      const res = await axios.post(`${API}/subscriptions/cancel`, {}, { params: { role: activeRole || user?.role } });
      toast.success(res.data.message || t.subscriptionCancelled);
      fetchSubscriptionStatus();
    } catch (error) {
      const msg = error.response?.data?.detail || t.subscriptionCancelFailed;
      toast.error(msg);
    } finally {
      setCancellingSubscription(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/users/me`);
      const data = response.data;
      // Update userData state with fetched values
      setUserData({
        name: data.name || '',
        phone: data.phone || ''
      });
      setDocuments({
        profile_photo_url: data.profile_photo_url || null,
        company_logo_url: data.company_logo_url || null,
        business_license_url: null,
        id_document_url: null,
        insurance_url: null,
        id_verification: { country: '', documentType: '', documentUrl: '' }
      });
    } catch (error) {
      console.error('Failed to load user data');
    }
  };

  const fetchProfessionalProfile = async () => {
    try {
      const response = await axios.get(`${API}/professionals/me`);
      setProfile(response.data);
      setProData({
        skills: response.data.skills || [],
        certifications: response.data.certifications || [],
        hourly_rate: response.data.hourly_rate || '',
        bio: response.data.bio || '',
        languages: response.data.languages || ['en'],
        categories: response.data.categories || [],
        service_areas: response.data.service_areas || { type: 'cantons', values: [] }
      });
      // Update documents from professional profile
      setDocuments(prev => ({
        ...prev,
        profile_photo_url: response.data.profile_photo_url || prev.profile_photo_url,
        company_logo_url: response.data.company_logo_url || prev.company_logo_url
      }));
    } catch (error) {
      console.error('Failed to load profile');
    }
  };

  const saveUserSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/users/me`, userData);
      toast.success(t.profileUpdated);
      // Refetch to ensure data is in sync
      await fetchUserData();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const saveProfessionalProfile = async () => {
    setLoading(true);
    try {
      const profileData = {
        ...proData,
        profile_photo_url: documents.profile_photo_url,
        company_logo_url: documents.company_logo_url
      };
      if (profileData.hourly_rate === '' || profileData.hourly_rate == null) {
        delete profileData.hourly_rate;
      } else {
        profileData.hourly_rate = Number(profileData.hourly_rate);
      }
      
      // Save to professional profile
      await axios.put(`${API}/professionals/profile`, profileData);
      
      toast.success(t.profileUpdated);
      // Refetch to ensure data is in sync
      await fetchProfessionalProfile();
      await fetchUserData();
      setProfileRevision((value) => value + 1);
    } catch (error) {
      console.error('Profile save error:', error);
      toast.error(t.profileUpdateFailed);
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !proData.skills.includes(newSkill.trim())) {
      setProData({ ...proData, skills: [...proData.skills, newSkill.trim()] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setProData({ ...proData, skills: proData.skills.filter(s => s !== skill) });
  };

  const addCertification = () => {
    if (newCert.trim() && !proData.certifications.includes(newCert.trim())) {
      setProData({ ...proData, certifications: [...proData.certifications, newCert.trim()] });
      setNewCert('');
    }
  };

  const removeCertification = (cert) => {
    setProData({ ...proData, certifications: proData.certifications.filter(c => c !== cert) });
  };

  const updateDocument = async (field, value) => {
    console.log('updateDocument called:', { field, value });
    
    if (!value) {
      console.log('updateDocument: value is empty, skipping');
      return;
    }
    
    try {
      const updatePayload = { [field]: value };
      console.log('updateDocument: sending to API:', updatePayload);
      
      // Save to user profile
      const userResponse = await axios.put(`${API}/users/me`, updatePayload);
      console.log('updateDocument: user update response:', userResponse.data);
      
      // Also save to professional profile if user is a professional
      if (user?.role === 'professional') {
        try {
          const proResponse = await axios.put(`${API}/professionals/profile`, updatePayload);
          console.log('updateDocument: professional update response:', proResponse.data);
        } catch (proError) {
          console.log('Professional profile update skipped:', proError);
        }
      }
      
      setDocuments(prev => ({ ...prev, [field]: value }));
      toast.success(t.docSaved);
    } catch (error) {
      console.error('Document update error:', error);
      console.error('Error details:', error.response?.data);
      toast.error(t.docSaveFailed);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error(t.fillAllPassword);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t.passwordsNotMatch);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error(t.passwordMinLength);
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success(t.passwordChanged);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Failed to change password' : language === 'it' ? 'Cambio password fallito' : language === 'fr' ? 'Échec du changement de mot de passe' : 'Passwortänderung fehlgeschlagen'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error(t.deleteConfirm);
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/users/me`);
      toast.success(t.accountDeleted);
      
      // Logout and redirect
      setTimeout(() => {
        logout();
        navigate('/');
      }, 1500);
    } catch (error) {
      toast.error(t.accountDeleteFailed);
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const response = await axios.get(`${API}/users/me/export-data`);
      
      // Create and download JSON file
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `helply_my_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t.dataExported);
    } catch (error) {
      toast.error(t.dataExportFailed);
    } finally {
      setExportingData(false);
    }
  };

  const handleDataAccessRequest = async () => {
    if (!dataRequestReason.trim()) {
      toast.error(t.requestReason);
      return;
    }
    
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/users/me/data-access-request`, {
        reason: dataRequestReason
      });
      
      toast.success(t.requestSubmitted);
      setShowDataRequest(false);
      setDataRequestReason('');
    } catch (error) {
      toast.error(t.requestFailed);
    } finally {
      setSubmittingRequest(false);
    }
  };

  const content = {
    en: {
      settings: 'Profile Settings',
      back: 'Back',
      userInfo: 'User Information',
      name: 'Full Name',
      phone: 'Phone',
      save: 'Save Changes',
      saving: 'Saving...',
      add: 'Add',
      professionalProfile: 'Professional Profile',
      skills: 'Skills',
      addSkill: 'Add Skill',
      skillPlaceholder: 'e.g., Electrician, Plumber...',
      noSkills: 'No skills added yet. Add your first skill above!',
      certifications: 'Certifications',
      addCert: 'Add Certification',
      certPlaceholder: 'e.g., Licensed Electrician, Safety Certificate...',
      noCerts: 'No certifications added yet. Add your certifications above!',
      hourlyRate: 'Hourly Rate (CHF)',
      perHour: '/hour',
      bio: 'Bio',
      bioPlaceholder: 'Tell clients about yourself, your experience, and what makes you special...',
      characters: 'characters',
      languages: 'Languages',
      verificationDocs: 'Verification Documents',
      verificationStatus: 'Verification Status',
      uploadDoc: 'Upload Document',
      docUploaded: 'Uploaded',
      docMissing: 'Missing',
      profilePhoto: 'Profile Photo',
      companyLogo: 'Company Logo',
      businessLicense: 'Business License',
      idDocument: 'ID Document',
      insurance: 'Insurance Certificate',
      idVerification: 'ID Verification Details',
      completeVerification: 'Complete your verification to get verified badge faster',
      missingDocs: 'Missing Documents',
      dangerZone: 'Danger Zone',
      deleteAccount: 'Delete Account',
      deleteWarning: 'This action cannot be undone. This will permanently delete your account and remove all your data.',
      deleteConfirm: 'Type DELETE to confirm',
      confirmDelete: 'Confirm Deletion',
      cancel: 'Cancel',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      updatePassword: 'Update Password',
      passwordRequirements: 'Password must be at least 6 characters long',
      subscriptionManagement: 'Subscription Management',
      currentPlan: 'Current Plan',
      trial: 'Free Trial',
      notSubscribed: 'Not Subscribed',
      active: 'Active',
      cancelled: 'Cancelled',
      expired: 'Expired',
      daysRemaining: 'days remaining',
      endsOn: 'Ends on',
      cancelSubscription: 'Cancel Subscription',
      upgradePlan: 'Upgrade Plan',
      viewPlans: 'View Plans',
      dataPrivacy: 'Data & Privacy',
      downloadMyData: 'Download My Data',
      downloadDesc: 'Export all your personal data in JSON format',
      dataAccessRequest: 'Data Access Request',
      dataAccessDesc: 'Submit a formal request for your data (GDPR)',
      requestReason: 'Reason for request',
      requestReasonPlaceholder: 'Please explain why you need this data...',
      submitRequest: 'Submit Request',
      gdprInfo: 'Under GDPR/FADP, you have the right to access, export, and delete your personal data.',
      docSaved: 'Document saved!',
      docSaveFailed: 'Failed to save document',
      fillAllPassword: 'Please fill all password fields',
      passwordsNotMatch: 'New passwords do not match',
      passwordMinLength: 'Password must be at least 6 characters',
      passwordChanged: 'Password changed successfully!',
      passwordChangeFailed: 'Failed to change password',
      profileUpdated: 'Profile updated!',
      profileUpdateFailed: 'Failed to update profile',
      accountDeleted: 'Account deleted',
      accountDeleteFailed: 'Failed to delete account',
      dataExported: 'Your data has been downloaded',
      dataExportFailed: 'Failed to export data',
      requestSubmitted: 'Data access request submitted',
      requestFailed: 'Failed to submit request',
      subscriptionCancelled: 'Subscription cancelled',
      subscriptionCancelFailed: 'Failed to cancel subscription',
      autoRenewal: 'Auto-Renewal',
      autoRenewalDesc: 'Automatically renew your subscription before it expires',
      autoRenewalEnabled: 'Auto-renewal enabled',
      autoRenewalDisabled: 'Auto-renewal disabled',
      savedCard: 'Saved Card',
      savedCardDesc: 'This card will be automatically charged when your subscription renews',
      noCardSaved: 'No card saved yet — your card will be saved automatically the next time you pay',
      removeCard: 'Remove Card',
      cardRemoved: 'Saved card removed',
      expiresIn: 'Expires in',
      days: 'days'
    },
    de: {
      settings: 'Profileinstellungen',
      back: 'Zurück',
      userInfo: 'Benutzerinformationen',
      name: 'Vollständiger Name',
      phone: 'Telefon',
      save: 'Änderungen speichern',
      saving: 'Speichern...',
      add: 'Hinzufügen',
      professionalProfile: 'Professionelles Profil',
      skills: 'Fähigkeiten',
      addSkill: 'Fähigkeit hinzufügen',
      skillPlaceholder: 'z.B. Elektriker, Klempner...',
      noSkills: 'Noch keine Fähigkeiten hinzugefügt.',
      certifications: 'Zertifizierungen',
      addCert: 'Zertifizierung hinzufügen',
      certPlaceholder: 'z.B. Geprüfter Elektriker, Sicherheitszertifikat...',
      noCerts: 'Noch keine Zertifizierungen hinzugefügt.',
      hourlyRate: 'Stundensatz (CHF)',
      perHour: '/Stunde',
      bio: 'Biografie',
      bioPlaceholder: 'Erzählen Sie Kunden von sich, Ihrer Erfahrung und was Sie besonders macht...',
      characters: 'Zeichen',
      languages: 'Sprachen',
      verificationDocs: 'Verifizierungsdokumente',
      verificationStatus: 'Verifizierungsstatus',
      uploadDoc: 'Dokument hochladen',
      docUploaded: 'Hochgeladen',
      docMissing: 'Fehlend',
      profilePhoto: 'Profilfoto',
      companyLogo: 'Firmenlogo',
      businessLicense: 'Gewerbeschein',
      idDocument: 'Ausweisdokument',
      insurance: 'Versicherungszertifikat',
      idVerification: 'ID-Verifizierungsdetails',
      completeVerification: 'Vervollständigen Sie Ihre Verifizierung, um schneller ein verifiziertes Abzeichen zu erhalten',
      missingDocs: 'Fehlende Dokumente',
      dangerZone: 'Gefahrenzone',
      deleteAccount: 'Konto löschen',
      deleteWarning: 'Diese Aktion kann nicht rückgängig gemacht werden. Dadurch werden Ihr Konto und alle Ihre Daten dauerhaft gelöscht.',
      deleteConfirm: 'Geben Sie DELETE ein, um zu bestätigen',
      confirmDelete: 'Löschung bestätigen',
      cancel: 'Abbrechen',
      changePassword: 'Passwort ändern',
      currentPassword: 'Aktuelles Passwort',
      newPassword: 'Neues Passwort',
      confirmNewPassword: 'Neues Passwort bestätigen',
      updatePassword: 'Passwort aktualisieren',
      passwordRequirements: 'Passwort muss mindestens 6 Zeichen lang sein',
      subscriptionManagement: 'Abonnementverwaltung',
      currentPlan: 'Aktueller Plan',
      trial: 'Kostenlose Testversion',
      notSubscribed: 'Nicht Abonniert',
      active: 'Aktiv',
      cancelled: 'Gekündigt',
      expired: 'Abgelaufen',
      daysRemaining: 'Tage verbleibend',
      endsOn: 'Endet am',
      cancelSubscription: 'Abonnement kündigen',
      upgradePlan: 'Plan upgraden',
      viewPlans: 'Pläne anzeigen',
      dataPrivacy: 'Daten & Datenschutz',
      downloadMyData: 'Meine Daten herunterladen',
      downloadDesc: 'Exportieren Sie alle Ihre persönlichen Daten im JSON-Format',
      dataAccessRequest: 'Datenzugriffsanfrage',
      dataAccessDesc: 'Formellen Antrag auf Ihre Daten stellen (DSGVO)',
      requestReason: 'Grund der Anfrage',
      requestReasonPlaceholder: 'Bitte erklären Sie, warum Sie diese Daten benötigen...',
      submitRequest: 'Anfrage senden',
      gdprInfo: 'Nach DSGVO/DSG haben Sie das Recht, auf Ihre persönlichen Daten zuzugreifen, diese zu exportieren und zu löschen.',
      docSaved: 'Dokument gespeichert!',
      docSaveFailed: 'Dokument konnte nicht gespeichert werden',
      fillAllPassword: 'Bitte füllen Sie alle Passwortfelder aus',
      passwordsNotMatch: 'Neue Passwörter stimmen nicht überein',
      passwordMinLength: 'Passwort muss mindestens 6 Zeichen lang sein',
      passwordChanged: 'Passwort erfolgreich geändert!',
      passwordChangeFailed: 'Passwort konnte nicht geändert werden',
      profileUpdated: 'Profil aktualisiert!',
      profileUpdateFailed: 'Profil konnte nicht aktualisiert werden',
      accountDeleted: 'Konto gelöscht',
      accountDeleteFailed: 'Konto konnte nicht gelöscht werden',
      dataExported: 'Ihre Daten wurden heruntergeladen',
      dataExportFailed: 'Daten konnten nicht exportiert werden',
      requestSubmitted: 'Datenzugriffsanfrage gesendet',
      requestFailed: 'Anfrage konnte nicht gesendet werden',
      subscriptionCancelled: 'Abonnement gekündigt',
      subscriptionCancelFailed: 'Abonnement konnte nicht gekündigt werden',
      autoRenewal: 'Automatische Verlängerung',
      autoRenewalDesc: 'Ihr Abonnement automatisch vor Ablauf verlängern',
      autoRenewalEnabled: 'Automatische Verlängerung aktiviert',
      autoRenewalDisabled: 'Automatische Verlängerung deaktiviert',
      savedCard: 'Gespeicherte Karte',
      savedCardDesc: 'Diese Karte wird automatisch belastet, wenn Ihr Abonnement verlängert wird',
      noCardSaved: 'Noch keine Karte gespeichert — Ihre Karte wird beim nächsten Zahlungsvorgang automatisch gespeichert',
      removeCard: 'Karte Entfernen',
      cardRemoved: 'Gespeicherte Karte entfernt',
      expiresIn: 'Läuft ab in',
      days: 'Tagen'
    },
    it: {
      settings: 'Impostazioni Profilo',
      back: 'Indietro',
      userInfo: 'Informazioni Utente',
      name: 'Nome Completo',
      phone: 'Telefono',
      save: 'Salva Modifiche',
      saving: 'Salvataggio...',
      add: 'Aggiungi',
      professionalProfile: 'Profilo Professionale',
      skills: 'Competenze',
      addSkill: 'Aggiungi Competenza',
      skillPlaceholder: 'es. Elettricista, Idraulico...',
      noSkills: 'Nessuna competenza aggiunta. Aggiungi la tua prima competenza!',
      certifications: 'Certificazioni',
      addCert: 'Aggiungi Certificazione',
      certPlaceholder: 'es. Elettricista Certificato, Certificato Sicurezza...',
      noCerts: 'Nessuna certificazione aggiunta. Aggiungi le tue certificazioni!',
      hourlyRate: 'Tariffa Oraria (CHF)',
      perHour: '/ora',
      bio: 'Biografia',
      bioPlaceholder: 'Racconta ai clienti di te, della tua esperienza e cosa ti rende speciale...',
      characters: 'caratteri',
      languages: 'Lingue',
      verificationDocs: 'Documenti di Verifica',
      verificationStatus: 'Stato Verifica',
      uploadDoc: 'Carica Documento',
      docUploaded: 'Caricato',
      docMissing: 'Mancante',
      profilePhoto: 'Foto Profilo',
      companyLogo: 'Logo Azienda',
      businessLicense: 'Licenza Commerciale',
      idDocument: 'Documento d\'Identità',
      insurance: 'Certificato Assicurazione',
      idVerification: 'Dettagli Verifica ID',
      completeVerification: 'Completa la verifica per ottenere il badge verificato più velocemente',
      missingDocs: 'Documenti Mancanti',
      dangerZone: 'Zona Pericolosa',
      deleteAccount: 'Elimina Account',
      deleteWarning: 'Questa azione non può essere annullata. Eliminerà permanentemente il tuo account e tutti i tuoi dati.',
      deleteConfirm: 'Digita DELETE per confermare',
      confirmDelete: 'Conferma Eliminazione',
      cancel: 'Annulla',
      changePassword: 'Cambia Password',
      currentPassword: 'Password Attuale',
      newPassword: 'Nuova Password',
      confirmNewPassword: 'Conferma Nuova Password',
      updatePassword: 'Aggiorna Password',
      passwordRequirements: 'La password deve essere di almeno 6 caratteri',
      subscriptionManagement: 'Gestione Abbonamento',
      currentPlan: 'Piano Attuale',
      trial: 'Prova Gratuita',
      notSubscribed: 'Non Abbonato',
      active: 'Attivo',
      cancelled: 'Annullato',
      expired: 'Scaduto',
      daysRemaining: 'giorni rimanenti',
      endsOn: 'Termina il',
      cancelSubscription: 'Annulla Abbonamento',
      upgradePlan: 'Aggiorna Piano',
      viewPlans: 'Visualizza Piani',
      dataPrivacy: 'Dati e Privacy',
      downloadMyData: 'Scarica i Miei Dati',
      downloadDesc: 'Esporta tutti i tuoi dati personali in formato JSON',
      dataAccessRequest: 'Richiesta Accesso Dati',
      dataAccessDesc: 'Invia una richiesta formale per i tuoi dati (GDPR)',
      requestReason: 'Motivo della richiesta',
      requestReasonPlaceholder: 'Per favore spiega perché hai bisogno di questi dati...',
      submitRequest: 'Invia Richiesta',
      gdprInfo: 'Secondo il GDPR/LPD, hai il diritto di accedere, esportare ed eliminare i tuoi dati personali.',
      docSaved: 'Documento salvato!',
      docSaveFailed: 'Impossibile salvare il documento',
      fillAllPassword: 'Per favore compila tutti i campi password',
      passwordsNotMatch: 'Le nuove password non corrispondono',
      passwordMinLength: 'La password deve essere di almeno 6 caratteri',
      passwordChanged: 'Password cambiata con successo!',
      passwordChangeFailed: 'Impossibile cambiare la password',
      profileUpdated: 'Profilo aggiornato!',
      profileUpdateFailed: 'Impossibile aggiornare il profilo',
      accountDeleted: 'Account eliminato',
      accountDeleteFailed: 'Impossibile eliminare l\'account',
      dataExported: 'I tuoi dati sono stati scaricati',
      dataExportFailed: 'Impossibile esportare i dati',
      requestSubmitted: 'Richiesta accesso dati inviata',
      requestFailed: 'Impossibile inviare la richiesta',
      subscriptionCancelled: 'Abbonamento annullato',
      subscriptionCancelFailed: 'Impossibile annullare l\'abbonamento',
      autoRenewal: 'Rinnovo Automatico',
      autoRenewalDesc: 'Rinnova automaticamente il tuo abbonamento prima della scadenza',
      autoRenewalEnabled: 'Rinnovo automatico abilitato',
      autoRenewalDisabled: 'Rinnovo automatico disabilitato',
      savedCard: 'Carta Salvata',
      savedCardDesc: 'Questa carta verrà addebitata automaticamente al rinnovo dell\'abbonamento',
      noCardSaved: 'Nessuna carta salvata — la tua carta verrà salvata automaticamente al prossimo pagamento',
      removeCard: 'Rimuovi Carta',
      cardRemoved: 'Carta salvata rimossa',
      expiresIn: 'Scade tra',
      days: 'giorni'
    },
    fr: {
      settings: 'Paramètres du Profil',
      back: 'Retour',
      userInfo: 'Informations Utilisateur',
      name: 'Nom Complet',
      phone: 'Téléphone',
      save: 'Enregistrer les Modifications',
      saving: 'Enregistrement...',
      add: 'Ajouter',
      professionalProfile: 'Profil Professionnel',
      skills: 'Compétences',
      addSkill: 'Ajouter une Compétence',
      skillPlaceholder: 'ex. Électricien, Plombier...',
      noSkills: 'Aucune compétence ajoutée. Ajoutez votre première compétence!',
      certifications: 'Certifications',
      addCert: 'Ajouter une Certification',
      certPlaceholder: 'ex. Électricien Certifié, Certificat de Sécurité...',
      noCerts: 'Aucune certification ajoutée. Ajoutez vos certifications!',
      hourlyRate: 'Tarif Horaire (CHF)',
      perHour: '/heure',
      bio: 'Biographie',
      bioPlaceholder: 'Parlez de vous aux clients, de votre expérience et de ce qui vous rend spécial...',
      characters: 'caractères',
      languages: 'Langues',
      verificationDocs: 'Documents de Vérification',
      verificationStatus: 'Statut de Vérification',
      uploadDoc: 'Télécharger un Document',
      docUploaded: 'Téléchargé',
      docMissing: 'Manquant',
      profilePhoto: 'Photo de Profil',
      companyLogo: 'Logo d\'Entreprise',
      businessLicense: 'Licence Commerciale',
      idDocument: 'Document d\'Identité',
      insurance: 'Certificat d\'Assurance',
      idVerification: 'Détails de Vérification d\'Identité',
      completeVerification: 'Complétez votre vérification pour obtenir le badge vérifié plus rapidement',
      missingDocs: 'Documents Manquants',
      dangerZone: 'Zone Dangereuse',
      deleteAccount: 'Supprimer le Compte',
      deleteWarning: 'Cette action ne peut pas être annulée. Cela supprimera définitivement votre compte et toutes vos données.',
      deleteConfirm: 'Tapez DELETE pour confirmer',
      confirmDelete: 'Confirmer la Suppression',
      cancel: 'Annuler',
      changePassword: 'Changer le Mot de Passe',
      currentPassword: 'Mot de Passe Actuel',
      newPassword: 'Nouveau Mot de Passe',
      confirmNewPassword: 'Confirmer le Nouveau Mot de Passe',
      updatePassword: 'Mettre à Jour le Mot de Passe',
      passwordRequirements: 'Le mot de passe doit comporter au moins 6 caractères',
      subscriptionManagement: 'Gestion de l\'Abonnement',
      currentPlan: 'Plan Actuel',
      trial: 'Essai Gratuit',
      notSubscribed: 'Non Abonné',
      active: 'Actif',
      cancelled: 'Annulé',
      expired: 'Expiré',
      daysRemaining: 'jours restants',
      endsOn: 'Se termine le',
      cancelSubscription: 'Annuler l\'Abonnement',
      upgradePlan: 'Mettre à Niveau le Plan',
      viewPlans: 'Voir les Plans',
      dataPrivacy: 'Données et Confidentialité',
      downloadMyData: 'Télécharger Mes Données',
      downloadDesc: 'Exportez toutes vos données personnelles au format JSON',
      dataAccessRequest: 'Demande d\'Accès aux Données',
      dataAccessDesc: 'Soumettre une demande formelle pour vos données (RGPD)',
      requestReason: 'Raison de la demande',
      requestReasonPlaceholder: 'Veuillez expliquer pourquoi vous avez besoin de ces données...',
      submitRequest: 'Soumettre la Demande',
      gdprInfo: 'En vertu du RGPD/LPD, vous avez le droit d\'accéder, d\'exporter et de supprimer vos données personnelles.',
      docSaved: 'Document enregistré!',
      docSaveFailed: 'Impossible d\'enregistrer le document',
      fillAllPassword: 'Veuillez remplir tous les champs de mot de passe',
      passwordsNotMatch: 'Les nouveaux mots de passe ne correspondent pas',
      passwordMinLength: 'Le mot de passe doit comporter au moins 6 caractères',
      passwordChanged: 'Mot de passe changé avec succès!',
      passwordChangeFailed: 'Impossible de changer le mot de passe',
      profileUpdated: 'Profil mis à jour!',
      profileUpdateFailed: 'Impossible de mettre à jour le profil',
      accountDeleted: 'Compte supprimé',
      accountDeleteFailed: 'Impossible de supprimer le compte',
      dataExported: 'Vos données ont été téléchargées',
      dataExportFailed: 'Impossible d\'exporter les données',
      requestSubmitted: 'Demande d\'accès aux données soumise',
      requestFailed: 'Impossible de soumettre la demande',
      subscriptionCancelled: 'Abonnement annulé',
      subscriptionCancelFailed: 'Impossible d\'annuler l\'abonnement',
      autoRenewal: 'Renouvellement Automatique',
      autoRenewalDesc: 'Renouveler automatiquement votre abonnement avant son expiration',
      autoRenewalEnabled: 'Renouvellement automatique activé',
      autoRenewalDisabled: 'Renouvellement automatique désactivé',
      savedCard: 'Carte Enregistrée',
      savedCardDesc: 'Cette carte sera débitée automatiquement lors du renouvellement de votre abonnement',
      noCardSaved: 'Aucune carte enregistrée — votre carte sera enregistrée automatiquement lors de votre prochain paiement',
      removeCard: 'Supprimer la Carte',
      cardRemoved: 'Carte enregistrée supprimée',
      expiresIn: 'Expire dans',
      days: 'jours'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="profile-settings-page">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t.back}
        </Button>

        <h1 className="text-3xl font-bold font-heading mb-6">{t.settings}</h1>

        {/* User Information */}
        <Card className="swiss-shadow-lg mb-6">
          <CardHeader>
            <CardTitle className="flex items-center font-heading">
              <User className="w-5 h-5 mr-2" />
              {t.userInfo}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t.name}</Label>
              <Input
                id="name"
                value={userData.name}
                onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                data-testid="name-input"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t.phone}</Label>
              <Input
                id="phone"
                value={userData.phone}
                onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
                data-testid="phone-input"
              />
            </div>
            <Button onClick={saveUserSettings} disabled={loading} data-testid="save-user-btn">
              {loading ? '...' : t.save}
            </Button>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="swiss-shadow-lg mb-6">
          <CardHeader>
            <CardTitle className="flex items-center font-heading">
              <Lock className="w-5 h-5 mr-2" />
              {t.changePassword}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">{t.currentPassword}</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="newPassword">{t.newPassword}</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t.passwordRequirements}</p>
            </div>
            <div>
              <Label htmlFor="confirmPassword">{t.confirmNewPassword}</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button onClick={handleChangePassword} disabled={loading} className="w-full" data-testid="update-password-btn">
              {loading ? '...' : t.updatePassword}
            </Button>
          </CardContent>
        </Card>

        {/* Professional Profile (only for professionals) */}
        {hasProfessionalProfile && (
          <Card className="swiss-shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center font-heading">
                <Briefcase className="w-5 h-5 mr-2" />
                {t.professionalProfile}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Skills */}
              <div>
                <Label className="text-base font-semibold mb-2 block">{t.skills}</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    placeholder={t.skillPlaceholder}
                    className="flex-1 h-11"
                    data-testid="skill-input"
                  />
                  <Button onClick={addSkill} size="sm" className="h-11 px-4 bg-green-600 hover:bg-green-700" data-testid="add-skill-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">{t.add}</span>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  {proData.skills.length === 0 ? (
                    <span className="text-gray-400 text-sm">{t.noSkills}</span>
                  ) : (
                    proData.skills.map((skill, idx) => (
                      <Badge key={idx} variant="secondary" className="text-sm py-1 px-3 bg-blue-100 text-blue-800" data-testid={`skill-badge-${idx}`}>
                        {skill}
                        <button onClick={() => removeSkill(skill)} className="ml-2 hover:text-red-600">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))
                  )}
                </div>
              </div>

              {/* Certifications */}
              <div>
                <Label className="text-base font-semibold mb-2 block">{t.certifications}</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newCert}
                    onChange={(e) => setNewCert(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                    placeholder={t.certPlaceholder}
                    className="flex-1 h-11"
                    data-testid="cert-input"
                  />
                  <Button onClick={addCertification} size="sm" className="h-11 px-4 bg-green-600 hover:bg-green-700" data-testid="add-cert-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">{t.add}</span>
                  </Button>
                </div>
                <div className="space-y-2 min-h-[40px]">
                  {proData.certifications.length === 0 ? (
                    <div className="p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300 text-gray-400 text-sm">
                      {language === 'en' ? 'No certifications added yet. Add your certifications above!' : language === 'it' ? 'Nessuna certificazione aggiunta ancora. Aggiungi le tue certificazioni sopra!' : 'Noch keine Zertifizierungen hinzugefügt.'}
                    </div>
                  ) : (
                    proData.certifications.map((cert, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200" data-testid={`cert-item-${idx}`}>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium">{cert}</span>
                        </div>
                        <button onClick={() => removeCertification(cert)} className="text-gray-500 hover:text-red-600 p-1">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Hourly Rate */}
              <ProfessionalServiceCoverageEditor value={proData} onChange={setProData} language={language} />

              {/* Hourly Rate */}
              <div>
                <Label htmlFor="rate" className="text-base font-semibold mb-2 block">{t.hourlyRate}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">CHF</span>
                  <Input
                    id="rate"
                    type="number"
                    value={proData.hourly_rate}
                    onChange={(e) => setProData({ ...proData, hourly_rate: e.target.value })}
                    className="pl-14 h-12 text-lg"
                    placeholder="0"
                    data-testid="rate-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{t.perHour}</span>
                </div>
              </div>

              {/* Bio */}
              <div>
                <Label htmlFor="bio" className="text-base font-semibold mb-2 block">{t.bio}</Label>
                <Textarea
                  id="bio"
                  value={proData.bio}
                  onChange={(e) => setProData({ ...proData, bio: e.target.value })}
                  rows={5}
                  className="text-base resize-y min-h-[120px]"
                  placeholder={t.bioPlaceholder}
                  data-testid="bio-input"
                />
                <p className="text-xs text-gray-500 mt-1">{proData.bio?.length || 0}/500 {t.characters}</p>
              </div>

              <Button onClick={saveProfessionalProfile} disabled={loading} className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700" data-testid="save-pro-btn">
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.saving}</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" /> {t.save}</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Verification Documents Section */}
        {false && (
          <Card className="mt-6">
            <CardHeader className="bg-blue-50">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                {t.verificationDocs}
              </CardTitle>
              <p className="text-sm text-gray-600 mt-2">{t.completeVerification}</p>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Document Status Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Profile Photo */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-600" />
                    <span className="font-medium">{t.profilePhoto}</span>
                  </div>
                  {documents.profile_photo_url ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t.docUploaded}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {t.docMissing}
                    </Badge>
                  )}
                </div>

                {/* Company Logo */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-gray-600" />
                    <span className="font-medium">{t.companyLogo}</span>
                  </div>
                  {documents.company_logo_url ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t.docUploaded}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500">
                      {t.docMissing}
                    </Badge>
                  )}
                </div>

                {/* Business License */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="font-medium">{t.businessLicense}</span>
                  </div>
                  {documents.business_license_url ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t.docUploaded}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {t.docMissing}
                    </Badge>
                  )}
                </div>

                {/* ID Document */}
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-600" />
                    <span className="font-medium">{t.idDocument}</span>
                  </div>
                  {documents.id_document_url ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {t.docUploaded}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {t.docMissing}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Missing Documents Alert */}
              {(!documents.profile_photo_url || !documents.business_license_url || !documents.id_document_url) && (
                <Alert className="border-orange-200 bg-orange-50">
                  <AlertTriangle className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    <strong>{t.missingDocs}:</strong>{' '}
                    {!documents.profile_photo_url && t.profilePhoto}
                    {!documents.profile_photo_url && (!documents.business_license_url || !documents.id_document_url) && ', '}
                    {!documents.business_license_url && t.businessLicense}
                    {!documents.business_license_url && !documents.id_document_url && ', '}
                    {!documents.id_document_url && t.idDocument}
                  </AlertDescription>
                </Alert>
              )}

              {/* Upload Missing Documents */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  {t.uploadDoc}
                </h4>

                {!documents.profile_photo_url && (
                  <FileUploadPlaceholder
                    label={t.profilePhoto}
                    accept="image/*"
                    value={documents.profile_photo_url}
                    onChange={(url) => updateDocument('profile_photo_url', url)}
                    language={language}
                    userId={user?.id || 'temp'}
                    folder="profiles"
                  />
                )}

                {!documents.company_logo_url && (
                  <FileUploadPlaceholder
                    label={`${t.companyLogo} (Optional)`}
                    accept="image/*"
                    value={documents.company_logo_url}
                    onChange={(url) => updateDocument('company_logo_url', url)}
                    language={language}
                    userId={user?.id || 'temp'}
                    folder="profiles"
                  />
                )}

                {!documents.business_license_url && (
                  <FileUploadPlaceholder
                    label={t.businessLicense}
                    accept="image/*,.pdf"
                    value={documents.business_license_url}
                    onChange={(url) => updateDocument('business_license_url', url)}
                    language={language}
                    userId={user?.id || 'temp'}
                    folder="verification_docs"
                  />
                )}

                {!documents.id_document_url && (
                  <div>
                    <IDVerificationForm
                      language={language}
                      userId={user?.id || 'temp'}
                      value={documents.id_verification}
                      onChange={(verificationData) => {
                        // Update local state
                        setDocuments({ ...documents, id_verification: verificationData, id_document_url: verificationData.documentUrl });
                        
                        // Only save to backend if documentUrl has a value
                        if (verificationData.documentUrl) {
                          updateDocument('id_verification', verificationData);
                          updateDocument('id_document_url', verificationData.documentUrl);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {hasProfessionalProfile && (
          <Card className="mt-6 swiss-shadow-lg" data-testid="verification-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Shield className="h-5 w-5 text-blue-700" />
                {t.verificationDocs}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProfessionalVerificationPanel API={API} user={user} language={language} profileRevision={profileRevision} />
            </CardContent>
          </Card>
        )}

        {/* Subscription Management Section */}
        <Card className="border-purple-200 mt-6">
          <CardHeader className="bg-purple-50">
            <CardTitle className="text-purple-700 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {t.subscriptionManagement}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {subscriptionStatus ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-semibold text-gray-900">{t.currentPlan}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={
                        subscriptionStatus.status === 'active' ? 'bg-green-100 text-green-800' :
                        subscriptionStatus.status === 'trial' ? 'bg-blue-100 text-blue-800' :
                        subscriptionStatus.status === 'cancelled' ? 'bg-orange-100 text-orange-800' :
                        subscriptionStatus.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }>
                        {subscriptionStatus.status === 'active' ? t.active :
                         subscriptionStatus.status === 'trial' ? t.trial :
                         subscriptionStatus.status === 'cancelled' ? t.cancelled :
                         subscriptionStatus.status === 'inactive' ? t.notSubscribed :
                         t.expired}
                      </Badge>
                      {subscriptionStatus.plan && (
                        <span className="text-sm text-gray-600">
                          ({subscriptionStatus.plan === 'monthly' ? 'Monthly' : 'Yearly'})
                        </span>
                      )}
                    </div>
                    {subscriptionStatus.trial_days_remaining !== null && subscriptionStatus.status === 'trial' && (
                      <p className="text-sm text-gray-600 mt-1">
                        {subscriptionStatus.trial_days_remaining} {t.daysRemaining}
                      </p>
                    )}
                    {subscriptionStatus.subscription_ends_at && subscriptionStatus.status === 'active' && (
                      <p className="text-sm text-gray-600 mt-1">
                        {language === 'de' ? 'Verlängert am' : language === 'it' ? 'Rinnovo il' : language === 'fr' ? 'Renouvellement le' : 'Renews on'}: <strong>{new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString()}</strong>
                      </p>
                    )}
                    {subscriptionStatus.subscription_ends_at && subscriptionStatus.status === 'cancelled' && (
                      <p className="text-sm text-orange-600 mt-1">
                        {language === 'de' ? 'Zugang bis' : language === 'it' ? 'Accesso fino al' : language === 'fr' ? 'Accès jusqu\'au' : 'Access until'}: {new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {subscriptionStatus.status === 'active' && (
                      subscriptionStatus.can_cancel?.allowed ? (
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={handleCancelSubscription}
                          disabled={cancellingSubscription}
                          data-testid="cancel-subscription-btn"
                        >
                          {cancellingSubscription ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          {t.cancelSubscription}
                        </Button>
                      ) : subscriptionStatus.can_cancel?.reason === 'within_7_days' ? (
                        <div className="text-right">
                          <span className="text-xs text-orange-600 font-medium px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg block">
                            {language === 'de' ? `Kündigung gesperrt (noch ${subscriptionStatus.can_cancel.days_left} Tage)` :
                             language === 'it' ? `Cancellazione bloccata (${subscriptionStatus.can_cancel.days_left} giorni)` :
                             language === 'fr' ? `Annulation bloquée (${subscriptionStatus.can_cancel.days_left} jour(s))` :
                             `Cancel window closed (${subscriptionStatus.can_cancel.days_left} day(s) left)`}
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            {language === 'de' ? 'Kündigung nur bis 7 Tage vor Verlängerung möglich' :
                             language === 'it' ? 'Cancellazione possibile solo fino a 7 giorni prima' :
                             language === 'fr' ? 'Annulation possible jusqu\'à 7 jours avant' :
                             'Cancellable up to 7 days before renewal'}
                          </p>
                        </div>
                      ) : null
                    )}
                    {(subscriptionStatus.status === 'trial' || subscriptionStatus.status === 'cancelled' || subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'inactive') && (
                      <Button
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => navigate('/subscription-plans')}
                        data-testid="renew-subscription-btn"
                      >
                        {subscriptionStatus.status === 'trial' ? t.upgradePlan : t.viewPlans}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Auto-Renewal + Saved Card */}
                {subscriptionStatus.status === 'active' && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-4">
                    <div className="flex items-start gap-3 justify-between">
                      <div className="flex items-start gap-3">
                        <RefreshCw className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">{t.autoRenewal}</h4>
                          <p className="text-sm text-gray-600 mt-0.5">{t.autoRenewalDesc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={autoRenewal}
                        onCheckedChange={handleToggleAutoRenewal}
                        disabled={togglingAutoRenewal}
                        data-testid="auto-renewal-toggle"
                      />
                    </div>

                    <div className="pt-3 border-t border-blue-200 flex items-center justify-between gap-3">
                      {savedCard?.has_card ? (
                        <>
                          <div>
                            <p className="text-sm font-medium text-gray-900" data-testid="saved-card-display">{t.savedCard}: {savedCard.card_display}</p>
                            <p className="text-xs text-gray-500">{t.savedCardDesc}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 flex-shrink-0"
                            onClick={handleRemoveCard}
                            disabled={removingCard}
                            data-testid="remove-saved-card-btn"
                          >
                            {removingCard ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                            {t.removeCard}
                          </Button>
                        </>
                      ) : (
                        <p className="text-xs text-gray-500" data-testid="no-saved-card-msg">{t.noCardSaved}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data & Privacy Section */}
        <Card className="border-blue-200 mt-6">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-blue-700 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {t.dataPrivacy}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <Alert className="border-blue-200 bg-blue-50">
              <Shield className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                {t.gdprInfo}
              </AlertDescription>
            </Alert>

            {/* Download My Data */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-semibold text-gray-900">{t.downloadMyData}</h4>
                <p className="text-sm text-gray-600">{t.downloadDesc}</p>
              </div>
              <Button 
                onClick={handleExportData}
                disabled={exportingData}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {exportingData ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {t.downloadMyData}
              </Button>
            </div>

            {/* Data Access Request */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">{t.dataAccessRequest}</h4>
                  <p className="text-sm text-gray-600">{t.dataAccessDesc}</p>
                </div>
                {!showDataRequest && (
                  <Button 
                    variant="outline"
                    onClick={() => setShowDataRequest(true)}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t.dataAccessRequest}
                  </Button>
                )}
              </div>
              
              {showDataRequest && (
                <div className="space-y-3 pt-2 border-t">
                  <div>
                    <Label htmlFor="request-reason">{t.requestReason}</Label>
                    <Textarea
                      id="request-reason"
                      value={dataRequestReason}
                      onChange={(e) => setDataRequestReason(e.target.value)}
                      placeholder={t.requestReasonPlaceholder}
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDataRequest(false);
                        setDataRequestReason('');
                      }}
                    >
                      {t.cancel}
                    </Button>
                    <Button
                      onClick={handleDataAccessRequest}
                      disabled={submittingRequest || !dataRequestReason.trim()}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {submittingRequest ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : null}
                      {t.submitRequest}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Account Roles Section */}
        <Card className="border-gray-200 mt-6">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-gray-800 flex items-center gap-2">
              <ArrowLeftRight className="w-5 h-5 text-gray-600" />
              {language === 'de' ? 'Kontorollen' : language === 'it' ? 'Ruoli Account' : language === 'fr' ? 'Rôles du Compte' : 'Account Roles'}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500 mb-4">
              {language === 'de' ? 'Verwalten Sie Ihre Rollen auf Helply. Sie können sowohl als Kunde als auch als Fachkraft tätig sein.' :
               language === 'it' ? 'Gestisci i tuoi ruoli su Helply. Puoi essere sia Cliente che Professionista.' :
               language === 'fr' ? 'Gérez vos rôles sur Helply. Vous pouvez être à la fois Client et Professionnel.' :
               'Manage your roles on Helply. You can be both a Client and a Professional on one account.'}
            </p>
            <div className="space-y-3">
              {/* Active roles */}
              {(user?.roles || [user?.role]).filter(Boolean).map((role) => (
                <div key={role} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                  <div className="flex items-center gap-3">
                    {role === 'professional' ? (
                      <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                        <Briefcase className="w-4 h-4 text-red-600" />
                      </div>
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {role === 'professional'
                          ? (language === 'de' ? 'Fachkraft-Profil' : language === 'it' ? 'Profilo Professionista' : language === 'fr' ? 'Profil Professionnel' : 'Professional Profile')
                          : (language === 'de' ? 'Kunden-Profil' : language === 'it' ? 'Profilo Cliente' : language === 'fr' ? 'Profil Client' : 'Client Profile')}
                      </p>
                      <p className="text-xs text-gray-400">
                        {language === 'de' ? 'Aktiv' : language === 'it' ? 'Attivo' : language === 'fr' ? 'Actif' : 'Active'}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border-0">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {language === 'de' ? 'Aktiv' : language === 'it' ? 'Attivo' : language === 'fr' ? 'Actif' : 'Active'}
                  </Badge>
                </div>
              ))}

              {/* Add missing role */}
              {(() => {
                const roles = user?.roles || [user?.role];
                const hasProfessional = roles.includes('professional');
                const hasClient = roles.includes('client');
                const missingRole = !hasProfessional ? 'professional' : !hasClient ? 'client' : null;
                if (!missingRole) return null;
                return (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-dashed border-gray-300 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${missingRole === 'professional' ? 'bg-red-50' : 'bg-blue-50'}`}>
                        {missingRole === 'professional' ? (
                          <Briefcase className="w-4 h-4 text-red-400" />
                        ) : (
                          <User className="w-4 h-4 text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-500 text-sm">
                          {missingRole === 'professional'
                            ? (language === 'de' ? 'Fachkraft-Profil' : language === 'it' ? 'Profilo Professionista' : language === 'fr' ? 'Profil Professionnel' : 'Professional Profile')
                            : (language === 'de' ? 'Kunden-Profil' : language === 'it' ? 'Profilo Cliente' : language === 'fr' ? 'Profil Client' : 'Client Profile')}
                        </p>
                        <p className="text-xs text-gray-400">
                          {missingRole === 'professional'
                            ? (language === 'de' ? 'CHF 99/Jahr' : 'CHF 99/year')
                            : (language === 'de' ? 'CHF 12/Jahr' : 'CHF 12/year')}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/add-role/${missingRole}`)}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="settings-add-role-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {language === 'de' ? 'Hinzufügen' : language === 'it' ? 'Aggiungi' : language === 'fr' ? 'Ajouter' : 'Add Role'}
                    </Button>
                  </div>
                );
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone - Delete Account */}
        <Card className="border-red-200 mt-6">
          <CardHeader className="bg-red-50">
            <CardTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              {t.dangerZone}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {!showDeleteConfirm ? (
              <div className="space-y-4">
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {t.deleteWarning}
                  </AlertDescription>
                </Alert>
                <Button 
                  variant="destructive" 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full"
                  data-testid="delete-account-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t.deleteAccount}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Alert className="border-red-200 bg-red-50">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {t.deleteWarning}
                  </AlertDescription>
                </Alert>
                <div>
                  <Label htmlFor="delete-confirm" className="text-red-700 font-semibold">
                    {t.deleteConfirm}
                  </Label>
                  <Input
                    id="delete-confirm"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="DELETE"
                    className="mt-2 border-red-300 focus:border-red-500"
                    data-testid="delete-confirm-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                    }}
                    className="flex-1"
                    data-testid="cancel-delete-btn"
                  >
                    {t.cancel}
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleDeleteAccount}
                    disabled={loading || deleteConfirmText !== 'DELETE'}
                    className="flex-1"
                    data-testid="confirm-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {loading ? '...' : t.confirmDelete}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileSettings;